// Consolidated dashboard landing page. Replaces the previous
// stack-of-cards (CelebrationStrip, SmartActionChips, CountdownHero,
// WeatherTravelCard, RecentResultsCard, TeamChemistryCard,
// QuickRsvpList, ActivityFeed, UpcomingTournamentFixtures,
// TournamentDashboardHero, Latest Announcements, SplitsRollup,
// CricketCareerCard) with four clear zones:
//
//   Zone 1 — Hero         (greeting + win streak + match + weather)
//   Zone 2 — Needs you    (actionable items only; empty when caught up)
//   Zone 3 — Workspace    (Standings · Upcoming · Recent, 3 columns)
//   Zone 4 — Everything else  (compact jump-to chips)
//
// Mobile stacks the whole thing into one column via auto-fit grids.

import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays, Clock, MapPin, Trophy,
  ChevronRight, Megaphone, Receipt, Gift, Gavel,
  Sun, Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning, CloudSun,
  BarChart3, Flame
} from "lucide-react";
import { api } from "../api";
import { useActiveTeam } from "./ActiveTeam";
import { useTheme } from "./Theme";
import { easternToMs, fmtMatchTime } from "../utils/time";

// ──────────────────────────────────────────────────────────────────
// Small helpers
// ──────────────────────────────────────────────────────────────────

function getGreeting() {
  const h = new Date().getHours();
  if (h < 5)  return "Up late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Hey";
  if (h < 21) return "Good evening";
  return "Up late";
}
function fmtDay(iso) {
  if (!iso) return "TBD";
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00-04:00` : iso);
  if (isNaN(d)) return "TBD";
  return d.toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    weekday: "short", month: "short", day: "numeric"
  });
}
function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
// Returns {d, h, m, isLive} or null.
function countdownParts(ms) {
  if (!Number.isFinite(ms)) return null;
  const diff = ms - Date.now();
  if (diff < -2 * 60 * 60 * 1000) return null;
  const t = Math.max(0, Math.floor(diff / 1000));
  return {
    d: Math.floor(t / 86400),
    h: Math.floor((t % 86400) / 3600),
    m: Math.floor((t % 3600) / 60),
    isLive: diff <= 0
  };
}
function WxIcon({ code, size = 36 }) {
  const base = (code || "").slice(0, 2);
  const props = { size, strokeWidth: 1.75 };
  if (base === "01") return <Sun {...props}/>;
  if (base === "02") return <CloudSun {...props}/>;
  if (base === "03" || base === "04") return <Cloud {...props}/>;
  if (base === "09" || base === "10") return <CloudRain {...props}/>;
  if (base === "11") return <CloudLightning {...props}/>;
  if (base === "13") return <CloudSnow {...props}/>;
  if (base === "50") return <CloudFog {...props}/>;
  return <Cloud {...props}/>;
}

// ──────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────

export default function DashboardHome({ state, user, navigate, reload }) {
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();

  // Minute-ticker for the countdown.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Secondary async data: active tournament fixtures + standings + my splits.
  const [tourney, setTourney] = useState({ loaded: false, next: null, upcoming: [], standings: null });
  const [weather, setWeather] = useState(null);
  const [splits, setSplits] = useState([]);

  // Load tournament data once.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const tournaments = await api.listTournaments().catch(() => []);
        const active = (tournaments || [])
          .filter(t => t.status !== "archived" && t.status !== "completed");
        if (!alive || active.length === 0) {
          if (alive) setTourney({ loaded: true, next: null, upcoming: [], standings: null });
          return;
        }
        const perT = await Promise.all(active.map(async t => ({
          tournament: t,
          fixtures: (await api.listTournamentFixtures(t.id).catch(() => [])) || []
        })));
        if (!alive) return;
        const now = Date.now();
        const all = [];
        for (const { tournament, fixtures } of perT) {
          for (const f of fixtures) {
            if (f.status !== "scheduled") continue;
            const ms = f.scheduledAt ? new Date(f.scheduledAt).getTime() : NaN;
            if (!Number.isFinite(ms)) continue;
            if (ms < now - 2 * 60 * 60 * 1000) continue;
            all.push({ fixture: f, tournament, ms });
          }
        }
        all.sort((a, b) => a.ms - b.ms);
        const next = all[0] || null;
        const standings = next
          ? await api.getTournamentStandings(next.tournament.id).catch(() => null)
          : null;
        if (!alive) return;
        setTourney({
          loaded: true,
          next,
          upcoming: all.slice(0, 5),
          standings
        });
      } catch {
        if (alive) setTourney({ loaded: true, next: null, upcoming: [], standings: null });
      }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  // Weather is a lazy follow-up once we know the chosen match's venue.
  const nextTournamentMs = tourney.next?.ms || null;
  useEffect(() => {
    const next = tourney.next;
    if (!next) { setWeather(null); return; }
    const venue = next.fixture.court || next.tournament?.defaultVenue;
    const scheduledAt = next.fixture.scheduledAt;
    if (!venue || !scheduledAt) { setWeather(null); return; }
    const d = new Date(scheduledAt);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    let alive = true;
    (async () => {
      try {
        const wx = await api.weatherForecast({ venue, date, time });
        if (alive) setWeather(wx);
      } catch { if (alive) setWeather(null); }
    })();
    return () => { alive = false; };
  }, [tourney.next?.fixture?.id]);

  // Splits (for Zone 2 "pay $X" chip + Zone 4 count).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await api.listMySplits().catch(() => null);
        if (!alive) return;
        setSplits(Array.isArray(list) ? list : (list?.open || list?.splits || []));
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  // ── Derived data ────────────────────────────────────────────────

  const nextStateMatch = useMemo(() => {
    const now = Date.now();
    const scoped = isAllTeams
      ? state.matches
      : state.matches.filter(m => m.teamId === activeTeamId);
    return scoped
      .map(m => ({ m, ms: easternToMs(m.date, m.time || "12:00") }))
      .filter(x => Number.isFinite(x.ms) && x.ms > now)
      .sort((a, b) => a.ms - b.ms)[0] || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.matches, isAllTeams, activeTeamId]);

  // Pick whichever is sooner — a tournament fixture or a regular match.
  const next = useMemo(() => {
    const tMs = tourney.next?.ms ?? null;
    const sMs = nextStateMatch?.ms ?? null;
    if (tMs && sMs) {
      return tMs <= sMs
        ? { kind: "tournament", ms: tMs }
        : { kind: "match", ms: sMs };
    }
    if (tMs) return { kind: "tournament", ms: tMs };
    if (sMs) return { kind: "match", ms: sMs };
    return null;
  }, [tourney.next, nextStateMatch]);

  const cd = useMemo(() => (next ? countdownParts(next.ms) : null), [next, tick]);

  // Win streak over the user's active team's recent completed matches.
  const streak = useMemo(() => {
    if (!activeTeamId) return 0;
    const completed = state.matches
      .filter(m => m.teamId === activeTeamId && m.result && m.result !== "Upcoming")
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    let n = 0;
    for (const m of completed) {
      if (m.result === "Won") n++;
      else break;
    }
    return n;
  }, [state.matches, activeTeamId]);

  // Zone 2 — "Needs you" items.
  const needs = useMemo(() => {
    const items = [];

    // Pending RSVP on next state.match
    if (nextStateMatch && user?.id) {
      const team = state.teams.find(t => t.id === nextStateMatch.m.teamId);
      const onRoster = team?.players?.includes(user.id);
      const has = state.availability.some(a => a.matchId === nextStateMatch.m.id && a.userId === user.id);
      if (onRoster && !has) {
        items.push({
          kind: "urgent",
          icon: <CalendarDays size={16}/>,
          title: "RSVP needed",
          sub: `vs ${nextStateMatch.m.opponent}`,
          onClick: () => navigate?.("availability")
        });
      }
    }

    // Pending RSVP on next tournament fixture
    if (tourney.next && user?.id) {
      const f = tourney.next.fixture;
      // We don't yet have team rosters in this scope; the tournament
      // detail page handles the actual RSVP. Only show the chip if we
      // know the viewer is on either team.
      const myTeams = (state.teams || []).filter(t => (t.players || []).includes(user.id)).map(t => t.id);
      if (myTeams.length > 0) {
        // Permissive: the fixture may or may not involve the viewer's
        // tournament team roster; always surface the opportunity.
        items.push({
          kind: "urgent",
          icon: <Trophy size={16}/>,
          title: "Tournament RSVP",
          sub: `${tourney.next.fixture.homeTeamName || "TBD"} vs ${tourney.next.fixture.awayTeamName || "TBD"}`,
          onClick: () => navigate?.("tournament-detail", tourney.next.tournament.id)
        });
      }
    }

    // Pay-you-owe splits
    const myOwes = [];
    for (const s of splits) {
      const parts = s.participants || s.lines || [];
      const mine = parts.find(p => (p.userId || p.user_id) === user?.id);
      const paid = mine && (mine.isPaid || mine.is_paid);
      if (mine && !paid) {
        myOwes.push({
          id: s.id,
          title: s.title || s.name,
          amount: Math.round(Number(mine.shareAmount || mine.share_amount || 0))
        });
      }
    }
    for (const o of myOwes.slice(0, 1)) {
      items.push({
        kind: "gold",
        icon: <Receipt size={16}/>,
        title: `Pay $${o.amount}`,
        sub: o.title,
        onClick: () => navigate?.("splits")
      });
    }

    // Birthdays today
    const today = new Date();
    for (const m of (state.members || [])) {
      const dob = m.dob || m.birthDate;
      if (!dob || m.id === user?.id) continue;
      const b = new Date(dob);
      if (isNaN(b)) continue;
      if (b.getMonth() === today.getMonth() && b.getDate() === today.getDate()) {
        items.push({
          kind: "mute",
          icon: <Gift size={16}/>,
          title: `${m.name.split(" ")[0]}'s birthday`,
          sub: "say hi today"
        });
        break;
      }
    }

    // Umpire polls
    if ((user?.roles || []).includes("UMPIRE")) {
      items.push({
        kind: "gold",
        icon: <Gavel size={16}/>,
        title: "Umpire polls",
        sub: "votes needed",
        onClick: () => navigate?.("umpire-polls")
      });
    }

    return items;
  }, [nextStateMatch, tourney.next, splits, state.availability, state.teams, state.members, user, navigate]);

  // Zone 3 — Workspace sources
  const standingsRows = (tourney.standings?.rows || []).slice(0, 3);

  const upcomingItems = useMemo(() => {
    const items = [];
    // Tournament fixtures first (feature focus).
    for (const u of (tourney.upcoming || []).slice(0, 3)) {
      items.push({
        key: `t-${u.fixture.id}`,
        title: `${u.fixture.homeTeamName || "TBD"} vs ${u.fixture.awayTeamName || "TBD"}`,
        sub: `${fmtDay(u.fixture.scheduledAt)} · ${u.tournament.name}${u.fixture.roundLabel ? ` · ${u.fixture.roundLabel}` : ""}`,
        onClick: () => navigate?.("tournament-detail", u.tournament.id)
      });
    }
    // Regular upcoming matches, filling to 3 total.
    if (items.length < 3) {
      const now = Date.now();
      const more = state.matches
        .filter(m => (isAllTeams ? true : m.teamId === activeTeamId))
        .map(m => ({ m, ms: easternToMs(m.date, m.time || "12:00") }))
        .filter(x => Number.isFinite(x.ms) && x.ms > now)
        .sort((a, b) => a.ms - b.ms)
        .slice(0, 3 - items.length);
      for (const x of more) {
        items.push({
          key: `m-${x.m.id}`,
          title: `vs ${x.m.opponent}`,
          sub: `${fmtDay(x.m.date)} · ${x.m.time ? `${fmtMatchTime(x.m.time)} ET` : "TBD"}`,
          onClick: () => navigate?.("schedule")
        });
      }
    }
    return items;
  }, [tourney.upcoming, state.matches, isAllTeams, activeTeamId, navigate]);

  const recentItems = useMemo(() => {
    const now = Date.now();
    const scoped = isAllTeams
      ? state.matches
      : state.matches.filter(m => m.teamId === activeTeamId);
    const past = scoped
      .map(m => ({ m, ms: easternToMs(m.date, m.time || "12:00") }))
      .filter(x => Number.isFinite(x.ms) && x.ms < now && x.m.result && x.m.result !== "Upcoming")
      .sort((a, b) => b.ms - a.ms)
      .slice(0, 3);
    return past.map(x => ({
      key: `r-${x.m.id}`,
      title: `${x.m.result} vs ${x.m.opponent}`,
      sub: `${fmtDay(x.m.date)}${x.m.score ? ` · ${x.m.score}` : ""}`,
      kind: x.m.result === "Won" ? "green" : x.m.result === "Lost" ? "red" : "mute",
      onClick: () => navigate?.("schedule")
    }));
  }, [state.matches, isAllTeams, activeTeamId, navigate]);

  // Zone 4 — Secondary chip counts
  const annCount = (state.announcements || []).length;
  const openSplitsCount = splits.filter(s => {
    const parts = s.participants || s.lines || [];
    const mine = parts.find(p => (p.userId || p.user_id) === user?.id);
    return mine && !(mine.isPaid || mine.is_paid);
  }).length;
  const upcomingBirthdays = useMemo(() => {
    const now = new Date();
    const in7 = new Date(); in7.setDate(now.getDate() + 7);
    let count = 0;
    for (const m of (state.members || [])) {
      const dob = m.dob || m.birthDate;
      if (!dob) continue;
      const b = new Date(dob);
      if (isNaN(b)) continue;
      const thisYear = new Date(now.getFullYear(), b.getMonth(), b.getDate());
      if (thisYear < now) thisYear.setFullYear(now.getFullYear() + 1);
      if (thisYear <= in7) count++;
    }
    return count;
  }, [state.members]);

  // ── Styles (one object so themes stay consistent) ───────────────
  const { theme } = useTheme();
  const S = useStyles(theme);

  const firstName = user?.name?.split(" ")[0] || user?.name || "there";
  const teamLabel = isAllTeams ? "All Teams" : (activeTeam?.name || "");

  const nextMatchObj = next?.kind === "tournament"
    ? {
        home: tourney.next.fixture.homeTeamName,
        away: tourney.next.fixture.awayTeamName,
        homeColor: tourney.next.fixture.homeTeamColor,
        awayColor: tourney.next.fixture.awayTeamColor,
        venue: tourney.next.fixture.court || tourney.next.tournament?.defaultVenue,
        scheduledAt: tourney.next.fixture.scheduledAt,
        label: `${tourney.next.tournament.name}${tourney.next.fixture.roundLabel ? ` · ${tourney.next.fixture.roundLabel}` : ""}`,
        onOpen: () => navigate?.("tournament-detail", tourney.next.tournament.id)
      }
    : nextStateMatch
    ? {
        home: isAllTeams ? "Pitt Gabruz" : (activeTeam?.name || "Home"),
        away: nextStateMatch.m.opponent,
        venue: nextStateMatch.m.venue,
        scheduledAt: null, // we fmt from date+time below
        date: nextStateMatch.m.date,
        time: nextStateMatch.m.time,
        label: nextStateMatch.m.sport || "Match",
        onOpen: () => navigate?.("schedule")
      }
    : null;

  return (
    <div className="dh-root" style={S.root}>

      {/* ───────── Zone 1 — Hero ───────── */}
      <section className="dh-hero" style={S.hero}>
        <div style={S.heroLeft}>
          <div style={S.eyebrow}>{getGreeting()} · {fmtDay(new Date().toISOString())}</div>
          <h1 style={S.heroName}>Welcome back, {firstName}.</h1>
          <div style={S.heroSub}>
            {teamLabel && <span>{teamLabel}</span>}
            {streak >= 2 && (
              <span style={{ color: "var(--green, #4FB76A)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Flame size={13}/> <b>{streak}</b>-match win streak
              </span>
            )}
            {streak < 2 && nextMatchObj && (
              <span style={S.heroSubItem}>Next up — <b>{nextMatchObj.away}</b></span>
            )}
          </div>
        </div>
        <div style={S.heroRight}>
          {nextMatchObj ? (
            <HeroMatchCard match={nextMatchObj} cd={cd} weather={weather} S={S}/>
          ) : (
            <div style={S.heroCardEmpty}>
              <div style={S.heroCardEmptyLabel}>No matches on the schedule</div>
              <small style={{ color: "var(--muted)" }}>Check back soon.</small>
            </div>
          )}
        </div>
      </section>

      {/* ───────── Zone 2 — Needs you ───────── */}
      {needs.length > 0 && (
        <section>
          <ZoneHeader label="Needs you" count={needs.length}/>
          <div style={S.priorityGrid}>
            {needs.map((it, i) => (
              <button
                key={i}
                type="button"
                onClick={it.onClick}
                style={{
                  ...S.priorityCard,
                  ...(it.kind === "urgent" ? S.priorityUrgent : it.kind === "gold" ? S.priorityGold : {})
                }}
              >
                <span style={{
                  ...S.priorityIco,
                  ...(it.kind === "urgent" ? S.priorityIcoUrgent : it.kind === "gold" ? S.priorityIcoGold : {})
                }}>{it.icon}</span>
                <span className="dh-label" style={S.priorityBody}>
                  <b>{it.title}</b>
                  <small>{it.sub}</small>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ───────── Zone 3 — Workspace ───────── */}
      <section>
        <ZoneHeader label="The workspace"/>
        <div style={S.workspace}>

          <WsCard title="Standings" ctaLabel="Full →" onCta={tourney.next ? () => navigate?.("tournament-detail", tourney.next.tournament.id) : null} S={S}>
            {standingsRows.length === 0 ? (
              <EmptyLine>Table fills as fixtures complete.</EmptyLine>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {standingsRows.map(r => (
                  <div key={r.teamId} style={{ ...S.standRow, ...(r.rank === 1 ? S.standRow1 : {}) }}>
                    <span style={{ ...S.standRank, ...(r.rank === 1 ? S.standRank1 : {}) }}>{r.rank}</span>
                    <span style={S.standTeam}>
                      <span style={{ ...S.dot, background: r.teamColor || "#6E6F6C" }}/>
                      <b style={S.standTeamName}>{r.teamName}</b>
                    </span>
                    <span style={S.standWl}>{r.wins}-{r.losses}</span>
                    <span style={S.standPts}>{r.tournamentPoints}</span>
                  </div>
                ))}
              </div>
            )}
          </WsCard>

          <WsCard title="Upcoming" ctaLabel="Schedule →" onCta={() => navigate?.("schedule")} S={S}>
            {upcomingItems.length === 0 ? (
              <EmptyLine>No matches scheduled.</EmptyLine>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {upcomingItems.map(it => (
                  <button key={it.key} type="button" onClick={it.onClick} className="dh-label" style={S.fixRow}>
                    <b>{it.title}</b>
                    <small>{it.sub}</small>
                  </button>
                ))}
              </div>
            )}
          </WsCard>

          <WsCard title="Recent" ctaLabel="History →" onCta={() => navigate?.("schedule")} S={S}>
            {recentItems.length === 0 ? (
              <EmptyLine>Nothing played yet.</EmptyLine>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {recentItems.map(it => (
                  <button key={it.key} type="button" onClick={it.onClick} style={S.actRow}>
                    <span style={{
                      ...S.actIco,
                      ...(it.kind === "green" ? S.actIcoGreen : it.kind === "red" ? S.actIcoRed : {})
                    }}>
                      {it.kind === "green" ? "W" : it.kind === "red" ? "L" : "·"}
                    </span>
                    <span className="dh-label" style={S.actBody}>
                      <b>{it.title}</b>
                      <small>{it.sub}</small>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </WsCard>

        </div>
      </section>

      {/* ───────── Zone 4 — Everything else ───────── */}
      <section>
        <ZoneHeader label="Everything else"/>
        <div style={S.secondary}>
          <SecChip
            icon={<Megaphone size={16}/>}
            title="Announcements"
            sub={annCount ? `${annCount} total` : "none"}
            onClick={() => navigate?.("announcements")}
            S={S}
          />
          <SecChip
            icon={<Receipt size={16}/>}
            title="Splits"
            sub={openSplitsCount ? `${openSplitsCount} open` : "all settled"}
            onClick={() => navigate?.("splits")}
            S={S}
          />
          <SecChip
            icon={<Gift size={16}/>}
            title="Birthdays"
            sub={upcomingBirthdays ? `${upcomingBirthdays} this week` : "none"}
            onClick={() => navigate?.("members")}
            S={S}
          />
          {activeTeam?.code === "ROWDYS_CRICKET" && (
            <SecChip
              icon={<BarChart3 size={16}/>}
              title="My career"
              sub="Rowdys stats"
              onClick={() => navigate?.("profile")}
              S={S}
            />
          )}
          {(user?.roles || []).includes("UMPIRE") && (
            <SecChip
              icon={<Gavel size={16}/>}
              title="Umpire polls"
              sub="open votes"
              onClick={() => navigate?.("umpire-polls")}
              S={S}
            />
          )}
        </div>
      </section>

    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────

function ZoneHeader({ label, count }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, margin: "0 0 10px",
      fontSize: 10.5, letterSpacing: "0.26em", textTransform: "uppercase",
      color: "var(--muted)", fontWeight: 700
    }}>
      <span style={{
        flex: "0 0 auto",
        color: "var(--gold, #F0C34A)"
      }}>{label}{count ? ` · ${count}` : ""}</span>
      <span style={{
        flex: 1, height: 1,
        background: "linear-gradient(90deg, var(--line, rgba(255,255,255,0.08)), transparent)"
      }}/>
    </div>
  );
}

function EmptyLine({ children }) {
  return (
    <p style={{ color: "var(--muted)", fontSize: 12.5, fontStyle: "italic", margin: 0, padding: "6px 2px" }}>
      {children}
    </p>
  );
}

function WsCard({ title, ctaLabel, onCta, children, S }) {
  return (
    <article style={S.wsCard}>
      <header style={S.wsH}>
        <span>{title}</span>
        {onCta && ctaLabel && (
          <button type="button" onClick={onCta} style={S.wsHCta}>{ctaLabel}</button>
        )}
      </header>
      {children}
    </article>
  );
}

function SecChip({ icon, title, sub, onClick, S }) {
  return (
    <button type="button" onClick={onClick} style={S.secCard}>
      <span style={S.secIco}>{icon}</span>
      <span className="dh-label" style={S.secBody}>
        <b>{title}</b>
        <small>{sub}</small>
      </span>
      <ChevronRight size={14} style={{ color: "var(--muted)" }}/>
    </button>
  );
}

function HeroMatchCard({ match, cd, weather, S }) {
  const forecast = weather?.now || weather?.forecast || null;
  const temp = forecast?.temperature != null ? Math.round(forecast.temperature) : null;
  const live = cd?.isLive;

  const scheduledAt = match.scheduledAt || (match.date ? `${match.date}T${(match.time || "12:00")}:00-04:00` : null);
  const dateText = scheduledAt ? fmtDay(scheduledAt) : (match.date ? fmtDay(match.date) : "TBD");
  const timeText = scheduledAt ? fmtTime(scheduledAt) : (match.time ? `${fmtMatchTime(match.time)} ET` : "");

  return (
    <article
      className="dh-hero-card"
      style={S.heroCard}
      onClick={match.onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={e => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), match.onOpen?.())}
    >
      <header style={S.heroCardHead}>
        <small>Next match{match.label ? ` · ${match.label}` : ""}</small>
        {cd && (
          <span style={{ ...S.heroCardCd, color: live ? "var(--green, #4FB76A)" : "#FFF" }}>
            {live
              ? "LIVE"
              : cd.d > 0
                ? <>{cd.d}d {cd.h}h</>
                : <>{cd.h}h {cd.m}m</>}
          </span>
        )}
      </header>

      <div style={S.heroCardTeams}>
        <div style={{ textAlign: "right", minWidth: 0 }}>
          <div style={{ ...S.heroCardTeam, color: match.homeColor || "#FFF" }}>{match.home || "TBD"}</div>
          <div style={S.heroCardSlot}>Home</div>
        </div>
        <div style={S.heroCardVs}>VS</div>
        <div style={{ textAlign: "left", minWidth: 0 }}>
          <div style={{ ...S.heroCardTeam, color: match.awayColor || "#FFF" }}>{match.away || "TBD"}</div>
          <div style={S.heroCardSlot}>Away</div>
        </div>
      </div>

      <footer style={S.heroCardFoot}>
        <span style={S.heroCardMeta}><CalendarDays size={12}/> {dateText}</span>
        {timeText && <span style={S.heroCardMeta}><Clock size={12}/> {timeText}</span>}
        {match.venue && <span style={S.heroCardMeta}><MapPin size={12}/> {match.venue}</span>}
        {temp != null && (
          <span style={{ ...S.heroCardMeta, color: "var(--gold, #F0C34A)" }}>
            <WxIcon code={forecast?.icon} size={14}/> {temp}°
          </span>
        )}
      </footer>
    </article>
  );
}

// ──────────────────────────────────────────────────────────────────
// Inline-style factory (one place to tune, theme vars drive the rest)
// ──────────────────────────────────────────────────────────────────

function useStyles(theme = "dark") {
  const light = theme === "light";
  return useMemo(() => ({
    root: {
      display: "flex", flexDirection: "column", gap: 20
    },

    // Hero
    hero: {
      padding: "22px 24px",
      background: light
        ? "linear-gradient(180deg, rgba(79, 70, 229, 0.04) 0%, rgba(79, 70, 229, 0.015) 60%, #ffffff 100%)"
        : "linear-gradient(180deg, rgba(200,167,70,0.08) 0%, rgba(30,58,138,0.08) 60%, var(--panel-2, rgba(255,255,255,0.02)) 100%)",
      border: light
        ? "1px solid rgba(15, 23, 42, 0.08)"
        : "1px solid rgba(200,167,70,0.22)",
      borderRadius: 14,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
      gap: 20, alignItems: "center",
      boxShadow: light ? "0 2px 8px rgba(15, 23, 42, 0.04)" : "none"
    },
    heroLeft: { minWidth: 0 },
    heroRight: { minWidth: 0 },
    eyebrow: {
      fontSize: 10.5, letterSpacing: "0.22em", textTransform: "uppercase",
      color: "var(--gold, #F0C34A)", fontWeight: 700, marginBottom: 6
    },
    heroName: {
      margin: 0,
      fontSize: "clamp(22px, 3vw, 30px)",
      fontWeight: 800, letterSpacing: "-0.02em",
      color: "var(--text, #FFF)"
    },
    heroSub: {
      marginTop: 10, fontSize: 13, color: "var(--muted)",
      display: "flex", flexWrap: "wrap", gap: 16
    },
    heroSubItem: {},

    heroCard: {
      padding: "14px 18px",
      background: light ? "#ffffff" : "rgba(0,0,0,0.22)",
      border: light
        ? "1px solid rgba(15, 23, 42, 0.08)"
        : "1px solid var(--line, rgba(255,255,255,0.08))",
      borderRadius: 12,
      boxShadow: light
        ? "0 1px 4px rgba(15, 23, 42, 0.04), 0 8px 20px -14px rgba(15, 23, 42, 0.10)"
        : "none",
      display: "flex", flexDirection: "column", gap: 10,
      cursor: "pointer",
      minHeight: 150
    },
    heroCardEmpty: {
      padding: "14px 18px",
      background: "var(--panel-2, rgba(255,255,255,0.03))",
      border: "1px dashed var(--line, rgba(255,255,255,0.08))",
      borderRadius: 12,
      minHeight: 150, display: "flex", flexDirection: "column", justifyContent: "center", gap: 4
    },
    heroCardEmptyLabel: {
      fontSize: 13, fontWeight: 700, color: "var(--text)"
    },
    heroCardHead: {
      display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap",
      fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase",
      color: "var(--gold, #F0C34A)", fontWeight: 700
    },
    heroCardCd: {
      fontSize: 18, fontWeight: 800, letterSpacing: "-0.015em",
      fontVariantNumeric: "tabular-nums",
      color: light ? "var(--text)" : "#ffffff"
    },
    heroCardTeams: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
      alignItems: "center", gap: 14,
      paddingBlock: 4
    },
    heroCardTeam: {
      fontSize: "clamp(14px, 2vw, 18px)", fontWeight: 800, letterSpacing: "-0.01em",
      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
    },
    heroCardSlot: {
      fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase",
      color: "var(--muted)", fontWeight: 700, marginTop: 2
    },
    heroCardVs: {
      fontSize: 13, fontWeight: 800, letterSpacing: "0.18em",
      color: "var(--gold, #F0C34A)"
    },
    heroCardFoot: {
      display: "flex", flexWrap: "wrap", gap: 12,
      fontSize: 11.5, color: "var(--muted)",
      paddingTop: 8, borderTop: "1px dashed var(--line, rgba(255,255,255,0.08))"
    },
    heroCardMeta: {
      display: "inline-flex", alignItems: "center", gap: 4
    },

    // Zone 2 priority
    priorityGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: 10
    },
    priorityCard: {
      padding: "12px 14px",
      background: "var(--panel-2, rgba(255,255,255,0.03))",
      border: "1px solid var(--line, rgba(255,255,255,0.08))",
      borderRadius: 10,
      display: "flex", alignItems: "center", gap: 10,
      cursor: "pointer", textAlign: "left",
      color: "inherit", font: "inherit"
    },
    priorityUrgent: {
      borderColor: "rgba(239,110,110,0.35)",
      background: "linear-gradient(180deg, rgba(239,110,110,0.06), transparent)"
    },
    priorityGold: {
      borderColor: "rgba(200,167,70,0.35)",
      background: "linear-gradient(180deg, rgba(200,167,70,0.06), transparent)"
    },
    priorityIco: {
      width: 36, height: 36, borderRadius: 10,
      background: "rgba(255,255,255,0.06)",
      display: "grid", placeItems: "center", flexShrink: 0,
      color: "var(--text)"
    },
    priorityIcoUrgent: {
      background: "rgba(239,110,110,0.18)", color: "#EF6E6E"
    },
    priorityIcoGold: {
      background: "rgba(200,167,70,0.18)", color: "var(--gold, #F0C34A)"
    },
    priorityBody: {
      display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1,
      textAlign: "left"
    },

    // Zone 3 workspace
    workspace: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
      gap: 14
    },
    wsCard: {
      padding: "14px 16px",
      background: "var(--panel-2, rgba(255,255,255,0.03))",
      border: "1px solid var(--line, rgba(255,255,255,0.08))",
      borderRadius: 12,
      display: "flex", flexDirection: "column", gap: 10,
      minWidth: 0
    },
    wsH: {
      display: "flex", justifyContent: "space-between", alignItems: "baseline",
      fontSize: 10.5, letterSpacing: "0.22em", textTransform: "uppercase",
      fontWeight: 700, color: "var(--gold, #F0C34A)"
    },
    wsHCta: {
      background: "transparent", border: 0, cursor: "pointer",
      fontSize: 10.5, fontWeight: 700, color: "var(--gold, #F0C34A)",
      letterSpacing: "0.05em"
    },

    // Standings mini
    standRow: {
      display: "grid",
      gridTemplateColumns: "22px minmax(0, 1fr) auto 38px",
      gap: 8, alignItems: "center",
      padding: "7px 10px",
      background: "rgba(255,255,255,0.02)",
      border: "1px solid var(--line, rgba(255,255,255,0.06))",
      borderRadius: 7, fontSize: 12.5
    },
    standRow1: {
      background: "rgba(200,167,70,0.08)",
      borderColor: "rgba(200,167,70,0.3)"
    },
    standRank: {
      fontWeight: 800, textAlign: "center", color: "var(--muted)",
      fontVariantNumeric: "tabular-nums", fontSize: 12
    },
    standRank1: { color: "var(--gold, #F0C34A)" },
    standTeam: {
      display: "flex", alignItems: "center", gap: 7, minWidth: 0
    },
    standTeamName: {
      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      fontWeight: 600, fontSize: 13
    },
    dot: {
      width: 9, height: 9, borderRadius: 2, flexShrink: 0
    },
    standWl: {
      fontSize: 11, color: "var(--muted)", fontVariantNumeric: "tabular-nums"
    },
    standPts: {
      textAlign: "right", fontWeight: 800, fontSize: 14,
      color: "var(--gold, #F0C34A)", fontVariantNumeric: "tabular-nums"
    },

    // Fixture mini
    fixRow: {
      padding: "9px 12px",
      background: "rgba(255,255,255,0.02)",
      border: "1px solid var(--line, rgba(255,255,255,0.06))",
      borderRadius: 7,
      display: "flex", flexDirection: "column", gap: 2,
      cursor: "pointer", color: "inherit", textAlign: "left",
      font: "inherit"
    },

    // Activity row
    actRow: {
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 2px",
      background: "transparent", border: 0, cursor: "pointer",
      color: "inherit", textAlign: "left", font: "inherit"
    },
    actIco: {
      width: 26, height: 26, borderRadius: 7,
      background: "rgba(255,255,255,0.06)",
      display: "grid", placeItems: "center",
      fontSize: 12, fontWeight: 800, color: "var(--muted)",
      flexShrink: 0
    },
    actIcoGreen: { background: "rgba(79,183,106,0.15)", color: "var(--green, #4FB76A)" },
    actIcoRed:   { background: "rgba(239,110,110,0.15)", color: "#EF6E6E" },
    actBody: {
      display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1
    },

    // Zone 4 secondary
    secondary: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: 10
    },
    secCard: {
      padding: "11px 14px",
      background: "var(--panel-2, rgba(255,255,255,0.03))",
      border: "1px solid var(--line, rgba(255,255,255,0.08))",
      borderRadius: 10,
      display: "flex", alignItems: "center", gap: 10,
      cursor: "pointer", textAlign: "left",
      color: "inherit", font: "inherit"
    },
    secIco: {
      width: 30, height: 30, borderRadius: 7,
      background: "rgba(200,167,70,0.14)",
      color: "var(--gold, #F0C34A)",
      display: "grid", placeItems: "center", flexShrink: 0
    },
    secBody: {
      display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1
    }
  }), []);
}

// Scoped CSS. The dashboard uses inline styles for layout but needs a
// handful of rules on text elements that inline styles can't reach
// easily (default <small> styling, color-on-bold).
if (typeof document !== "undefined" && !document.getElementById("dash-home-css")) {
  const el = document.createElement("style");
  el.id = "dash-home-css";
  el.textContent = `
    .dh-label > b {
      font-size: 13.5px;
      font-weight: 700;
      color: var(--text, #FFF);
      letter-spacing: -0.005em;
      line-height: 1.25;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
    }
    .dh-label > small {
      font-size: 11px;
      color: var(--muted);
      line-height: 1.3;
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .dh-root button { outline-offset: 2px; }
    .dh-root button:hover { filter: brightness(1.08); }
    @media (max-width: 720px) {
      .dh-root .dh-hero { padding: 18px 18px; }
    }
  `;
  document.head.appendChild(el);
}
